// Plan Management — shared presentational UI kit. Standardises the module onto the app's design
// system (index.css component classes + Inter + indigo primary + slate neutrals). Presentational
// only: no API calls, no business logic.
import React, { useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  AlertOctagon, AlertTriangle, ArrowRight, Ban, Check, ChevronDown, ChevronLeft, ChevronUp,
  ChevronsUpDown, Copy, Inbox, MoreHorizontal, PauseCircle, Pencil, RefreshCw, Search,
  X, XCircle,
} from 'lucide-react';
import { INV_STATUS, PIPELINE, statusLabel } from './pmStatus';
import { money, fmtDate, fmtDateTime } from '../../utils/format';

// Money and dates come from the app-wide formatter (utils/format.js), so a figure reads the same in
// Plan Management as everywhere else — to the cent, with the sign before the dollar ('-$2,556.70').
export { money, fmtDate, fmtDateTime };

// Right-aligned, tabular currency cell; negatives shown red.
export function Currency({ value, className, negativeRed = true }) {
  const n = Number(value || 0);
  return <span className={clsx('tabular-nums', negativeRed && n < 0 && 'text-red-600', className)}>{money(n)}</span>;
}

// Invoice status pill — single source of truth (maps via pmStatus).
export function StatusBadge({ status, className }) {
  return <span className={clsx('badge', INV_STATUS[status] || 'badge-gray', className)}>{statusLabel(status)}</span>;
}

// Dot accent per KPI (Attio/Linear: monochrome surface, one small colour cue).
const DOT = {
  primary: 'bg-primary-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500',
  rose: 'bg-rose-500', cyan: 'bg-cyan-500', indigo: 'bg-indigo-500', slate: 'bg-slate-400',
};

// A named accent's chip and top edge. 'primary' and 'slate' (the defaults) take the colour of the
// section the page belongs to instead (.hue-* on <main>, see components/Layout/routes.js).
const TILE_CHIP = {
  emerald: 'bg-emerald-50 text-emerald-600', amber: 'bg-amber-50 text-amber-600', rose: 'bg-rose-50 text-rose-600',
  cyan: 'bg-cyan-50 text-cyan-600', indigo: 'bg-indigo-50 text-indigo-600', violet: 'bg-violet-50 text-violet-600',
  sky: 'bg-sky-50 text-sky-600',
};
const TILE_EDGE = {
  emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500', cyan: 'bg-cyan-500',
  indigo: 'bg-indigo-500', violet: 'bg-violet-500', sky: 'bg-sky-500',
};

// KPI tile — white card with a thin top edge and icon chip in its colour, big tabular value.
export function KpiTile({ label, value, sub, accent = 'primary', icon: Icon, alert }) {
  const hued = !TILE_CHIP[accent];
  return (
    // A sentence-case label (no tracked capitals shouting above the number), the value in the
    // display face, one quiet line underneath. A tile turns rose only when its number is a problem.
    <div className={clsx('relative bg-white border rounded-xl p-4 shadow-pmcard overflow-hidden', alert ? 'border-rose-200' : 'border-line')}>
      <span className={clsx('absolute inset-x-0 top-0 h-[3px]', alert ? 'bg-rose-500' : hued ? 'hue-bar' : TILE_EDGE[accent])} aria-hidden="true" />
      <div className="flex items-start justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-ink-soft min-w-0 pt-0.5">
          {!Icon && <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', alert ? 'bg-rose-500' : hued ? 'hue-bar' : (DOT[accent] || DOT.primary))} />}
          <span className="truncate">{label}</span>
        </span>
        {Icon && (
          <span className={clsx('w-8 h-8 -mt-0.5 rounded-lg flex items-center justify-center shrink-0',
            alert ? 'bg-rose-50 text-rose-600' : hued ? 'hue-chip' : TILE_CHIP[accent])}>
            <Icon size={16} aria-hidden="true" />
          </span>
        )}
      </div>
      <div className={clsx('font-display text-[26px] font-bold tabular-nums leading-none tracking-[-0.02em] mt-2.5', alert ? 'text-rose-600' : 'text-ink')}>{value}</div>
      {sub && <div className="text-[12px] text-muted mt-2">{sub}</div>}
    </div>
  );
}

const BAR = { primary: 'bg-primary-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', rose: 'bg-rose-500', cyan: 'bg-cyan-500' };
export function ProgressBar({ pct, tone = 'primary', className }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return <div className={clsx('h-2 rounded-full bg-slate-100 overflow-hidden', className)}><div className={clsx('h-full rounded-full transition-all duration-300', BAR[tone] || BAR.primary)} style={{ width: `${p}%` }} /></div>;
}

// Category budget card — Budget / Spent / Remaining + % bar (turns amber/rose as it fills).
export function BudgetCard({ label, budget, spent, remaining }) {
  const b = Number(budget) || 0; const used = Number(spent) || 0;
  const pct = b > 0 ? (used / b) * 100 : 0;
  const tone = pct >= 100 ? 'rose' : pct >= 85 ? 'amber' : 'emerald';
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-medium text-slate-700 truncate">{label}</span>
        <Currency value={remaining} className={clsx('text-xs font-semibold', remaining < 0 ? 'text-rose-600' : 'text-emerald-600')} />
      </div>
      <ProgressBar pct={pct} tone={tone} />
      <div className="flex items-center justify-between mt-1.5 text-[11px] text-slate-500 tabular-nums">
        <span>{money(used)} used</span><span>of {money(b)}</span>
      </div>
    </div>
  );
}

export function SectionCard({ title, icon: Icon, actions, children, className, bodyClass }) {
  return (
    <div className={clsx('bg-white border border-slate-200 rounded-xl overflow-hidden', className)}>
      {(title || actions) && (
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            {Icon && <span className="w-7 h-7 rounded-lg hue-chip flex items-center justify-center shrink-0"><Icon size={15} aria-hidden="true" /></span>}
            {title && <span className="text-sm font-semibold text-slate-800 truncate">{title}</span>}
          </div>
          {actions}
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="py-14 px-6 text-center">
      {Icon && <span className="mx-auto w-14 h-14 rounded-2xl hue-chip flex items-center justify-center mb-3"><Icon size={26} aria-hidden="true" /></span>}
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      {hint && <p className="text-[13px] leading-relaxed text-slate-500 mt-1 max-w-sm mx-auto">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div className="p-4 space-y-2.5" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3 items-center">
          {Array.from({ length: cols }).map((_, c) => <div key={c} className={clsx('skeleton h-6', c === 0 ? 'w-8 shrink-0' : 'flex-1')} />)}
        </div>
      ))}
    </div>
  );
}

// A failed load is never shown as an empty list: it says it failed and offers a retry.
export function ErrorState({ message = "We couldn't load this. Check your connection, then try again.", onRetry }) {
  return (
    <div className="py-12 px-6 text-center" role="alert">
      <p className="text-sm font-medium text-rose-700">{message}</p>
      {onRetry && <button type="button" onClick={onRetry} className="btn-secondary text-sm mt-3">Try again</button>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   WORKBENCH KIT — queue-driven operator surface (PM redesign)

   The module used to be a dashboard plus a stack of full-page modals, so an
   operator working a queue ("18 blocked", "6 claim errors") lost the list on
   every hop. Everything below exists to support scan → open → act → next
   without leaving the list: a split pane, a dense table, and record surfaces
   that show ALL of an invoice's problems at once (spec 6.2).

   House rules kept from the top of this file: presentational only, named
   exports, index.css component classes (.btn-*, .badge-*, .card, .skeleton),
   slate surface + primary accent, colour reserved for STATE, tabular money.
   ══════════════════════════════════════════════════════════════════════════ */

// Semantic tones. emerald = good/paid · amber = waiting/attention ·
// rose = error/blocked · cyan = reconciled · primary = the one UI accent.
export const TONE_TEXT = {
  primary: 'text-primary-600', emerald: 'text-emerald-600', amber: 'text-amber-600',
  rose: 'text-rose-600', cyan: 'text-cyan-600', slate: 'text-slate-700', muted: 'text-slate-500',
};
const TONE_SOFT = {
  primary: 'bg-primary-50 text-primary-700 ring-1 ring-primary-100',
  emerald: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  amber: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
  rose: 'bg-rose-50 text-rose-700 ring-1 ring-rose-100',
  cyan: 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100',
  slate: 'bg-slate-100 text-slate-600 ring-1 ring-slate-200',
};
const TONE_BORDER = {
  primary: 'border-primary-200', emerald: 'border-emerald-200', amber: 'border-amber-200',
  rose: 'border-rose-200', cyan: 'border-cyan-200', slate: 'border-slate-200',
};
const TONE_HEAD = {
  primary: 'bg-primary-50 border-primary-100', emerald: 'bg-emerald-50 border-emerald-100',
  amber: 'bg-amber-50 border-amber-100', rose: 'bg-rose-50 border-rose-100',
  cyan: 'bg-cyan-50 border-cyan-100', slate: 'bg-slate-50 border-slate-100',
};

// Dates: fmtDate / fmtDateTime are imported from utils/format.js at the top of this file.

// Uppercase micro-label — column headers, stat labels, meta keys.
export function MicroLabel({ children, className, as: As = 'span' }) {
  return <As className={clsx('block text-[10.5px] font-semibold uppercase tracking-[0.07em] text-slate-500', className)}>{children}</As>;
}

// Checkbox with real indeterminate support (header "select all" needs it).
export function Checkbox({ checked, indeterminate, onChange, disabled, ariaLabel, className }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!indeterminate; }, [indeterminate]);
  return (
    <input
      ref={ref} type="checkbox" checked={!!checked} disabled={disabled} aria-label={ariaLabel}
      onChange={(e) => onChange && onChange(e.target.checked, e)}
      onClick={(e) => e.stopPropagation()}
      className={clsx('h-3.5 w-3.5 rounded border-slate-300 accent-primary-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40', className)}
    />
  );
}

// CSS-only tooltip. No portal, so keep it out of `overflow-hidden` ancestors.
export function Tooltip({ label, children, side = 'top', className }) {
  if (!label) return children;
  const pos = side === 'bottom' ? 'top-full mt-1.5' : side === 'left' ? 'right-full mr-1.5 top-1/2 -translate-y-1/2'
    : side === 'right' ? 'left-full ml-1.5 top-1/2 -translate-y-1/2' : 'bottom-full mb-1.5';
  const centre = side === 'top' || side === 'bottom' ? 'left-1/2 -translate-x-1/2' : '';
  return (
    <span className={clsx('relative inline-flex group/tt', className)}>
      {children}
      <span role="tooltip"
        className={clsx('tooltip pointer-events-none absolute z-50 whitespace-nowrap opacity-0 transition-opacity duration-100 motion-reduce:transition-none',
          'group-hover/tt:opacity-100 group-focus-within/tt:opacity-100', pos, centre)}>
        {label}
      </span>
    </span>
  );
}

// Copy-to-clipboard for the identifiers operators quote to the NDIA
// (ClaimReference, invoice number, batch ID).
export function CopyButton({ value, label, size = 13, className, title = 'Copy', ariaLabel }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => setDone(false), 1400);
    return () => clearTimeout(t);
  }, [done]);
  const copy = (e) => {
    e.stopPropagation();
    const text = String(value ?? '');
    if (!text) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => setDone(true)).catch(() => {});
    }
  };
  return (
    <button type="button" onClick={copy} title={title} aria-label={ariaLabel || `${title}${value ? ` ${value}` : ''}`}
      className={clsx('inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors motion-reduce:transition-none', className)}>
      {done ? <Check size={size} className="text-emerald-500" /> : <Copy size={size} />}
      {label && <span className="text-[11px] font-medium">{done ? 'Copied' : label}</span>}
    </button>
  );
}

// Kebab menu. Portalled + fixed so table/pane overflow never clips it.
export function OverflowMenu({ items, size = 15, label = 'More actions', icon: Icon = MoreHorizontal, className }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const close = useCallback(() => setPos(null), []);
  useEffect(() => {
    if (!pos) return undefined;
    const onDoc = (e) => { if (btnRef.current && btnRef.current.contains(e.target)) return; close(); };
    const onMove = () => close();
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      document.removeEventListener('keydown', onKey);
    };
  }, [pos, close]);
  const visible = (items || []).filter(Boolean);
  if (!visible.length) return null;
  const toggle = (e) => {
    e.stopPropagation();
    if (pos) { close(); return; }
    const r = e.currentTarget.getBoundingClientRect();
    const estH = visible.length * 34 + 12;
    const up = r.bottom + estH > window.innerHeight - 8;
    setPos({
      top: up ? undefined : r.bottom + 4,
      bottom: up ? window.innerHeight - r.top + 4 : undefined,
      right: Math.max(8, window.innerWidth - r.right),
    });
  };
  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle} aria-label={label} aria-haspopup="menu" aria-expanded={!!pos}
        className={clsx('p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors motion-reduce:transition-none', className)}>
        <Icon size={size} />
      </button>
      {pos && createPortal(
        <div role="menu" style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, right: pos.right, zIndex: 70 }}
          className="w-56 bg-white rounded-xl border border-slate-200 shadow-card-deep py-1.5 animate-fade-in motion-reduce:animate-none"
          onMouseDown={(e) => e.stopPropagation()}>
          {visible.map((it, i) => (it.divider ? <div key={`d${i}`} role="separator" className="my-1 border-t border-slate-100" /> : (
            <button key={it.label || i} type="button" role="menuitem" disabled={it.disabled}
              onClick={(e) => { e.stopPropagation(); close(); if (it.onClick) it.onClick(); }}
              className={clsx('w-full flex items-center gap-2.5 px-3.5 py-1.5 text-[13px] font-medium text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
                it.danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-600 hover:bg-slate-50')}>
              {it.icon && <it.icon size={14} className={clsx('shrink-0', it.danger ? 'text-rose-400' : 'text-slate-400')} />}
              <span className="truncate">{it.label}</span>
              {it.hint && <span className="ml-auto text-[10.5px] text-slate-300 tabular-nums">{it.hint}</span>}
            </button>
          )))}
        </div>,
        document.body,
      )}
    </>
  );
}

// ─── Toolbar row ───────────────────────────────────────────────────────────

export function SearchInput({ value = '', onChange, placeholder = 'Search', shortcut, className, size = 'md', ariaLabel }) {
  const ref = useRef(null);
  // A single-key shortcut ("/") is the queue-scanning affordance operators
  // expect; ignored while typing in another field.
  useEffect(() => {
    if (!shortcut) return undefined;
    const onKey = (e) => {
      if (e.key !== shortcut || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      e.preventDefault();
      if (ref.current) ref.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcut]);
  const h = size === 'sm' ? 'h-7 text-[12px]' : 'h-8 text-[13px]';
  return (
    <div className={clsx('relative', className)}>
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      <input
        ref={ref} type="text" value={value} placeholder={placeholder} aria-label={ariaLabel || placeholder}
        onChange={(e) => onChange && onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape' && value && onChange) { e.stopPropagation(); onChange(''); } }}
        className={clsx('w-full pl-8 pr-8 bg-white border border-slate-200 rounded-lg text-slate-800 placeholder:text-slate-400',
          'hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:ring-offset-0',
          'transition-colors motion-reduce:transition-none', h)}
      />
      {value ? (
        <button type="button" onClick={() => onChange && onChange('')} aria-label="Clear search"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
          <X size={12} />
        </button>
      ) : shortcut ? (
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] font-semibold text-slate-400 border border-slate-200 rounded px-1 leading-4">{shortcut}</kbd>
      ) : null}
    </div>
  );
}

// Queue chips with live counts — "All 214 · Blocked 18 · Claim error 6".
// `multi` (default) returns an array; single-select returns the scalar value.
// An option with value `null`/'' is treated as the "All" reset.
export function FilterChips({ options = [], value, onChange, multi = true, className, ariaLabel = 'Filter queue' }) {
  const sel = multi ? (Array.isArray(value) ? value : value == null ? [] : [value]) : value;
  const isOn = (v) => (multi ? sel.includes(v) : sel === v);
  const isAll = (v) => v == null || v === '' || v === 'all';
  const pick = (v) => {
    if (!onChange) return;
    if (!multi) { onChange(isOn(v) ? null : v); return; }
    if (isAll(v)) { onChange([]); return; }
    onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel.filter((x) => !isAll(x)), v]);
  };
  return (
    <div role="group" aria-label={ariaLabel} className={clsx('flex items-center gap-1.5 flex-wrap', className)}>
      {options.filter(Boolean).map((o) => {
        const on = isAll(o.value) ? (multi ? sel.length === 0 : sel == null) : isOn(o.value);
        return (
          <button
            key={String(o.value)} type="button" onClick={() => pick(o.value)} aria-pressed={on} disabled={o.disabled}
            className={clsx('inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-full border text-[12px] font-medium whitespace-nowrap',
              'transition-colors motion-reduce:transition-none disabled:opacity-40 disabled:cursor-not-allowed',
              on ? 'border-primary-300 bg-primary-50 text-primary-700'
                 : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300')}>
            {o.tone && <span className={clsx('w-1.5 h-1.5 rounded-full shrink-0', DOT[o.tone] || DOT.slate)} aria-hidden />}
            {o.label}
            {o.count != null && (
              <span className={clsx('tabular-nums text-[11px] font-semibold', on ? 'text-primary-600' : 'text-slate-500')}>{o.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// The bar above a table: title + count, search, chips, right-aligned actions.
// `sticky` keeps it pinned while the queue scrolls under it.
export function Toolbar({
  title, count, countLabel, icon: Icon, search, onSearch, searchPlaceholder = 'Search', searchShortcut,
  chips, actions, overflow, children, sticky = true, className,
}) {
  return (
    <div className={clsx('bg-white border-b border-slate-200 px-4 py-2.5 flex flex-col gap-2.5',
      sticky && 'sticky top-0 z-20', className)}>
      <div className="flex items-center gap-3 flex-wrap">
        {(title || count != null) && (
          <div className={clsx('flex gap-2 min-w-0', Icon ? 'items-center' : 'items-baseline')}>
            {Icon && (
              <span className="w-7 h-7 rounded-lg hue-chip flex items-center justify-center shrink-0" aria-hidden="true">
                <Icon size={14} />
              </span>
            )}
            {title && <h2 className="text-[13px] font-semibold text-slate-800 truncate">{title}</h2>}
            {count != null && (
              <span className="text-[12px] tabular-nums text-slate-500">
                {Number(count).toLocaleString('en-AU')}{countLabel ? ` ${countLabel}` : ''}
              </span>
            )}
          </div>
        )}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2 min-w-0">
          {onSearch && <SearchInput value={search} onChange={onSearch} placeholder={searchPlaceholder} shortcut={searchShortcut} className="w-52 sm:w-64" />}
          {actions}
          {overflow && overflow.length ? <OverflowMenu items={overflow} /> : null}
        </div>
      </div>
      {(chips || children) && <div className="flex items-center gap-2 flex-wrap">{chips}{children}</div>}
    </div>
  );
}

// Slides in over the bottom of the list when rows are selected. Kept white so
// the caller's ordinary .btn-secondary / .btn-primary children look native.
export function BulkBar({ count = 0, onClear, noun = 'selected', children, className }) {
  if (!count) return null;
  return (
    <div className={clsx('sticky bottom-3 z-30 mx-3 mt-2 animate-fade-up motion-reduce:animate-none', className)}>
      <div className="flex items-center gap-3 rounded-xl border border-slate-300 bg-white px-3 py-2 shadow-card-deep border-l-2 border-l-primary-500">
        <span className="text-[13px] font-semibold text-slate-800 tabular-nums whitespace-nowrap">
          {Number(count).toLocaleString('en-AU')} {noun}
        </span>
        {onClear && (
          <button type="button" onClick={onClear} className="text-[12px] font-medium text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline">
            Clear
          </button>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">{children}</div>
      </div>
    </div>
  );
}

// ─── DataTable ─────────────────────────────────────────────────────────────
// Dense, sortable, keyboard-navigable. Columns:
//   { key, header, width, align, money, truncate, sortable, sortKey, sortValue,
//     defaultDir, className, headerClassName, render(row, i) }
// Ordering is owned by the parent as soon as `onSortChange` is passed
// (server-side sort); without it the table sorts its own copy of `rows`.

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' };
const ROW_ACCENT = {
  primary: 'border-l-primary-500', emerald: 'border-l-emerald-400', amber: 'border-l-amber-400',
  rose: 'border-l-rose-400', cyan: 'border-l-cyan-400', slate: 'border-l-slate-300',
};

export function DataTable({
  columns = [], rows = [], rowKey = 'id',
  loading = false, error = null, onRetry, empty = {},
  sort, onSortChange, defaultSort = null,
  selectable = false, selected, onSelectedChange,
  onRowClick, activeRowKey = null, rowActions, rowTone,
  dense = false, stickyHeader = true, maxHeight, className, footer, label = 'Records',
}) {
  const cols = useMemo(() => columns.filter(Boolean), [columns]);
  const keyOf = useCallback((row, i) => {
    if (typeof rowKey === 'function') return rowKey(row, i);
    const v = row ? row[rowKey] : undefined;
    return v == null ? i : v;
  }, [rowKey]);

  const [innerSort, setInnerSort] = useState(defaultSort);
  const activeSort = sort || innerSort;
  const ordered = useMemo(() => {
    if (onSortChange || !activeSort || !activeSort.key) return rows;
    const col = cols.find((c) => (c.sortKey || c.key) === activeSort.key);
    const dir = activeSort.dir === 'desc' ? -1 : 1;
    const read = (r) => (col && col.sortValue ? col.sortValue(r) : (r ? r[activeSort.key] : undefined));
    return [...rows].sort((a, b) => {
      const x = read(a); const y = read(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;            // blanks always sink, either direction
      if (y == null) return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return String(x).localeCompare(String(y), 'en-AU', { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [rows, cols, activeSort, onSortChange]);

  const selectedSet = useMemo(() => new Set(selected || []), [selected]);
  const allKeys = useMemo(() => ordered.map((r, i) => keyOf(r, i)), [ordered, keyOf]);
  const allOn = allKeys.length > 0 && allKeys.every((k) => selectedSet.has(k));
  const someOn = !allOn && allKeys.some((k) => selectedSet.has(k));
  const toggleRow = useCallback((k) => {
    if (!onSelectedChange) return;
    const next = new Set(selectedSet);
    if (next.has(k)) next.delete(k); else next.add(k);
    onSelectedChange([...next]);
  }, [selectedSet, onSelectedChange]);

  // Roving tabindex: the open record (or the first row) is the single tab stop.
  const rowRefs = useRef([]);
  const tabIdx = useMemo(() => {
    const i = ordered.findIndex((r, n) => keyOf(r, n) === activeRowKey);
    return i >= 0 ? i : 0;
  }, [ordered, activeRowKey, keyOf]);
  const onRowKeyDown = (e, row, i) => {
    const focusAt = (n) => { const el = rowRefs.current[n]; if (el) { e.preventDefault(); el.focus(); } };
    if (e.key === 'ArrowDown') focusAt(i + 1);
    else if (e.key === 'ArrowUp') focusAt(i - 1);
    else if (e.key === 'Home') focusAt(0);
    else if (e.key === 'End') focusAt(ordered.length - 1);
    else if (e.key === 'Enter' && onRowClick) { e.preventDefault(); onRowClick(row, i); }
    else if ((e.key === ' ' || e.key === 'Spacebar') && selectable) { e.preventDefault(); toggleRow(keyOf(row, i)); }
  };

  const state = loading ? 'loading' : error ? 'error' : ordered.length === 0 ? 'empty' : 'rows';
  if (state !== 'rows') {
    return (
      <div className={clsx('bg-white', className)}>
        {state === 'loading' && <TableSkeleton rows={dense ? 9 : 6} cols={Math.min(Math.max(cols.length, 3), 7)} />}
        {state === 'error' && <ErrorState message={typeof error === 'string' ? error : (error && error.message) || undefined} onRetry={onRetry} />}
        {state === 'empty' && <EmptyState icon={empty.icon || Inbox} title={empty.title || 'Nothing in this queue'} hint={empty.hint} action={empty.action} />}
      </div>
    );
  }

  const handleSort = (c) => {
    const key = c.sortKey || c.key;
    const dir = activeSort && activeSort.key === key
      ? (activeSort.dir === 'asc' ? 'desc' : 'asc')
      : (c.defaultDir || (c.money ? 'desc' : 'asc'));
    if (onSortChange) onSortChange({ key, dir }); else setInnerSort({ key, dir });
  };
  const cell = dense ? 'py-1.5 px-3' : 'py-2.5 px-3';

  return (
    <div className={clsx('bg-white', maxHeight ? 'overflow-auto overscroll-contain' : 'overflow-x-auto', className)}
      style={maxHeight ? { maxHeight } : undefined}>
      <table className="w-full border-collapse text-[13px]">
        <caption className="sr-only">{label}</caption>
        <thead className={clsx('bg-white', stickyHeader && 'sticky top-0 z-10')}>
          <tr className="border-b border-slate-200">
            {selectable && (
              <th scope="col" className="w-9 pl-4 pr-1 py-2 text-left border-l-2 border-l-transparent">
                <Checkbox checked={allOn} indeterminate={someOn} ariaLabel="Select all rows"
                  onChange={() => onSelectedChange && onSelectedChange(allOn ? [] : allKeys)} />
              </th>
            )}
            {cols.map((c, ci) => {
              const key = c.sortKey || c.key;
              const on = activeSort && activeSort.key === key;
              const align = c.align || (c.money ? 'right' : 'left');
              return (
                <th key={c.key} scope="col" style={c.width ? { width: c.width } : undefined}
                  aria-sort={on ? (activeSort.dir === 'desc' ? 'descending' : 'ascending') : (c.sortable ? 'none' : undefined)}
                  className={clsx('py-2 px-3 font-medium text-[12px] whitespace-nowrap',
                    on ? 'text-slate-900' : 'text-slate-600', ALIGN[align], c.headerClassName,
                    ci === 0 && !selectable && 'border-l-2 border-l-transparent')}>
                  {c.sortable ? (
                    <button type="button" onClick={() => handleSort(c)}
                      className={clsx('inline-flex items-center gap-1 rounded hover:text-slate-700 transition-colors motion-reduce:transition-none',
                        align === 'right' && 'flex-row-reverse')}>
                      {c.header}
                      {on ? (activeSort.dir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)
                        : <ChevronsUpDown size={12} className="text-slate-300" />}
                    </button>
                  ) : c.header}
                </th>
              );
            })}
            {rowActions && <th scope="col" className="w-12 pr-3"><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {ordered.map((row, i) => {
            const k = keyOf(row, i);
            const isActive = activeRowKey != null && k === activeRowKey;
            const isSel = selectedSet.has(k);
            const accent = isActive ? ROW_ACCENT.primary : ROW_ACCENT[rowTone && rowTone(row)] || 'border-l-transparent';
            return (
              <tr
                key={k} ref={(el) => { rowRefs.current[i] = el; }} tabIndex={i === tabIdx ? 0 : -1}
                aria-selected={selectable ? isSel : undefined}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                onKeyDown={(e) => onRowKeyDown(e, row, i)}
                className={clsx('group border-b border-slate-100 border-l-2 outline-none transition-colors motion-reduce:transition-none',
                  accent, onRowClick && 'cursor-pointer',
                  isActive ? 'bg-primary-50/70' : isSel ? 'bg-primary-50/40' : 'hover:bg-slate-50',
                  'focus-visible:bg-primary-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400')}>
                {selectable && (
                  <td className={clsx('pl-4 pr-1', cell)}>
                    <Checkbox checked={isSel} ariaLabel="Select row" onChange={() => toggleRow(k)} />
                  </td>
                )}
                {cols.map((c) => {
                  const align = c.align || (c.money ? 'right' : 'left');
                  const v = c.render ? c.render(row, i) : (c.money ? <Currency value={row ? row[c.key] : 0} /> : (row ? row[c.key] : null));
                  return (
                    <td key={c.key}
                      className={clsx(cell, ALIGN[align], 'align-middle text-slate-700', c.money && 'tabular-nums',
                        c.truncate && 'max-w-0 truncate', c.className)}>
                      {v}
                    </td>
                  );
                })}
                {rowActions && (
                  <td className={clsx('pr-3 text-right', cell)}>
                    <div className={clsx('flex items-center justify-end gap-1 transition-opacity motion-reduce:transition-none',
                      'opacity-100',
                      isActive && 'sm:opacity-100')}>
                      {rowActions(row, i)}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        {footer && (
          <tfoot className="sticky bottom-0 bg-slate-50/95 backdrop-blur-sm">
            <tr className="border-t border-slate-200">
              <td colSpan={cols.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)} className="px-3 py-2 text-[12px] text-slate-600">{footer}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// ─── Split pane ────────────────────────────────────────────────────────────
// Queue on the left, open record on the right; both panes scroll on their own
// so the list never moves under the operator. Below `lg` it degrades to a
// stack: list, then detail replaces it (with a Back control).

const WorkbenchCtx = React.createContext({ detailOpen: false, closeDetail: undefined });

const LIST_W = {
  sm: 'lg:w-[340px] lg:shrink-0', md: 'lg:w-[420px] lg:shrink-0',
  lg: 'lg:w-[520px] lg:shrink-0', xl: 'lg:w-[640px] lg:shrink-0',
  half: 'lg:flex-1 lg:basis-0', wide: 'lg:flex-[1.6] lg:basis-0', full: 'lg:w-full',
};

// `offset` = whatever page chrome sits above the workbench (header + toolbar),
// so the panes fill exactly the rest of the viewport instead of guessing.
export function Workbench({ detailOpen = false, onCloseDetail, offset = '11rem', fill = true, children, className }) {
  const ctx = useMemo(() => ({ detailOpen, closeDetail: onCloseDetail }), [detailOpen, onCloseDetail]);
  return (
    <WorkbenchCtx.Provider value={ctx}>
      <div style={{ '--wb-offset': offset }}
        className={clsx('flex flex-col lg:flex-row items-stretch gap-4 min-h-0',
          fill && 'lg:h-[calc(100vh-var(--wb-offset))]', className)}>
        {children}
      </div>
    </WorkbenchCtx.Provider>
  );
}

// `scroll` = the pane owns one scrollbar (toolbars pin with `sticky`).
// `scroll={false}` = the children own their scrolling — use it when the table
// itself must scroll so its sticky header stays put under a fixed toolbar.
export function WorkbenchList({ width = 'wide', scroll = true, children, className, bodyClassName, label = 'Queue' }) {
  const { detailOpen } = useContext(WorkbenchCtx);
  return (
    <section aria-label={label}
      className={clsx('min-w-0 flex-1 flex-col bg-white border border-slate-200 rounded-xl overflow-hidden lg:h-full lg:min-h-0',
        LIST_W[width] || LIST_W.wide, detailOpen ? 'hidden lg:flex' : 'flex', className)}>
      <div className={clsx('flex-1 min-h-0 flex flex-col',
        scroll ? 'overflow-y-auto overscroll-contain' : 'overflow-hidden', bodyClassName)}>
        {children}
      </div>
    </section>
  );
}

// Renders `placeholder` on wide screens when nothing is open, so the layout
// never reflows as the operator moves down the queue.
export function WorkbenchDetail({ children, placeholder, backLabel = 'Back to queue', className, bodyClassName, label = 'Record' }) {
  const { detailOpen, closeDetail } = useContext(WorkbenchCtx);
  return (
    <section aria-label={label}
      className={clsx('min-w-0 flex-1 lg:basis-0 lg:min-w-[380px] flex-col bg-white border border-slate-200 rounded-xl overflow-hidden lg:h-full lg:min-h-0',
        detailOpen ? 'flex' : 'hidden lg:flex', className)}>
      {detailOpen && closeDetail && (
        <div className="lg:hidden border-b border-slate-100 px-2 py-1.5 shrink-0">
          <button type="button" onClick={closeDetail} className="btn-ghost btn-sm"><ChevronLeft size={14} />{backLabel}</button>
        </div>
      )}
      <div className={clsx('flex-1 min-h-0 overflow-y-auto overscroll-contain', bodyClassName)}>
        {detailOpen ? children : (placeholder || <EmptyState icon={Inbox} title="Nothing open" hint="Choose a row to see the full record here." />)}
      </div>
    </section>
  );
}

// ─── Detail drawer ─────────────────────────────────────────────────────────
// Right-side slide-over for a secondary record (provider, batch, participant)
// opened from inside the workbench — the split pane keeps its place underneath.

const DRAWER_W = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-3xl' };

export function DetailDrawer({
  open = true, onClose, title, subtitle, icon: Icon, status, statusNode, meta,
  width = 'lg', footer, children, className, bodyClassName,
}) {
  const panelRef = useRef(null);
  // Kept in a ref so an inline onClose doesn't re-run this effect (and steal focus) every render.
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => { onCloseRef.current = onClose; });
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || !onCloseRef.current) return;
      if (document.querySelector('[role="alertdialog"][aria-modal="true"]')) return; // a confirm handles its own Escape
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    const prevFocus = document.activeElement;
    document.body.style.overflow = 'hidden';
    if (panelRef.current) panelRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (prevFocus && prevFocus.focus) prevFocus.focus();
    };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 animate-fade-in motion-reduce:animate-none" onMouseDown={onClose}>
      <div
        ref={panelRef} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Record'} tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        className={clsx('h-full w-full bg-white flex flex-col shadow-modal outline-none animate-drawer-in motion-reduce:animate-none',
          DRAWER_W[width] || DRAWER_W.lg, className)}>
        <header className="px-5 py-3.5 border-b border-slate-200 flex items-start gap-3 shrink-0">
          {Icon && <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center shrink-0 mt-0.5"><Icon size={16} /></span>}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-[15px] font-semibold text-slate-900 truncate">{title}</h3>
              {statusNode || (status ? <StatusBadge status={status} /> : null)}
            </div>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5 truncate">{subtitle}</p>}
            {meta && meta.length > 0 && (
              <dl className="flex flex-wrap gap-x-6 gap-y-1.5 mt-3">
                {meta.filter(Boolean).map((m) => (
                  <div key={m.label}>
                    <MicroLabel as="dt">{m.label}</MicroLabel>
                    <dd className={clsx('text-[13px] text-slate-800 mt-0.5', m.money && 'tabular-nums')}>{m.value == null || m.value === '' ? '—' : m.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          {onClose && (
            <button type="button" onClick={onClose} aria-label="Close"
              className="p-1.5 -mr-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 shrink-0"><X size={18} /></button>
          )}
        </header>
        <div className={clsx('flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-4', bodyClassName)}>{children}</div>
        {footer && (
          <footer className="px-5 py-3 border-t border-slate-200 bg-slate-50/70 flex items-center justify-end gap-2 flex-wrap shrink-0">{footer}</footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ─── Status stepper ────────────────────────────────────────────────────────
// The forward pipeline comes from pmStatus (PIPELINE + statusLabel) so the
// stepper can never drift from the badges. Side states are NOT drawn as steps
// — pretending "On hold" is a stage hides that the invoice has left the path.
// They render as an off-path banner hanging off the last stage reached.

const SIDE_STATE = {
  on_hold: { tone: 'amber', icon: PauseCircle, after: 'created', blurb: 'Held. It stays here until every block clears — an invoice is claimed and paid whole.' },
  claim_error: { tone: 'rose', icon: AlertOctagon, after: 'claimed', blurb: 'The NDIA returned Rejected or Error. Correct it, then re-claim in a new batch.' },
  declined: { tone: 'rose', icon: XCircle, after: 'created', blurb: 'Declined and kept on record. Reinstating returns it to the approval step, not to Approved.' },
  cancelled: { tone: 'slate', icon: Ban, after: null, blurb: 'Cancelled. The funding commitment has been released.' },
};

export function StatusStepper({ status, steps = PIPELINE, anchorStatus, reason, action, className, compact = false }) {
  const side = SIDE_STATE[status];
  const anchor = side ? (anchorStatus || side.after) : status;
  const idx = anchor ? steps.indexOf(anchor) : -1;
  const SideIcon = side ? side.icon : null;
  return (
    <div className={className}>
      <div className="overflow-x-auto">
        <ol aria-label="Invoice lifecycle" className="flex items-center min-w-max">
          {steps.map((s, i) => {
            const done = i < idx;
            const current = i === idx;
            const dim = side && i >= idx;
            return (
              <li key={s} className="flex items-center">
                {i > 0 && (
                  <span aria-hidden
                    className={clsx('w-6 sm:w-9 mx-1 h-px', done || current ? (side ? 'bg-slate-200' : 'bg-primary-400') : 'bg-slate-200')} />
                )}
                <span className="flex items-center gap-1.5" aria-current={current && !side ? 'step' : undefined}>
                  <span className={clsx('w-4 h-4 rounded-full border flex items-center justify-center shrink-0',
                    done ? 'bg-primary-600 border-primary-600 text-white'
                      : current ? (side ? clsx('bg-white', TONE_BORDER[side.tone]) : 'bg-white border-primary-600')
                        : 'bg-white border-slate-300')}>
                    {done ? <Check size={10} strokeWidth={3} />
                      : current ? <span className={clsx('w-1.5 h-1.5 rounded-full', side ? DOT[side.tone] : 'bg-primary-600')} />
                        : null}
                  </span>
                  <span className={clsx('text-[11.5px] whitespace-nowrap', compact && i !== idx && 'hidden sm:inline',
                    current && !side ? 'font-semibold text-slate-900'
                      : done ? 'text-slate-600' : dim ? 'text-slate-300' : 'text-slate-400')}>
                    {statusLabel(s)}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      {side && (
        <div className={clsx('mt-3 flex items-start gap-2.5 rounded-lg border px-3 py-2.5', TONE_BORDER[side.tone], TONE_HEAD[side.tone])}>
          <SideIcon size={15} className={clsx('mt-0.5 shrink-0', TONE_TEXT[side.tone])} />
          <div className="min-w-0 flex-1">
            <p className={clsx('text-[12.5px] font-semibold', TONE_TEXT[side.tone])}>Off the main path — {statusLabel(status)}</p>
            <p className="text-[12px] text-slate-600 mt-0.5">{reason || side.blurb}</p>
          </div>
          {action && <div className="shrink-0">{action}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Timeline ──────────────────────────────────────────────────────────────
// "Everything is logged: who did what, when, and the before and after values."

export function Timeline({ items = [], emptyText = 'No activity recorded yet', dense = false, className }) {
  if (!items.length) return <p className={clsx('text-[12.5px] text-slate-400 py-3', className)}>{emptyText}</p>;
  return (
    <ol className={clsx('relative', className)}>
      <span aria-hidden className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
      {items.filter(Boolean).map((it, i) => {
        const ItIcon = it.icon;
        return (
          <li key={it.id || i} className={clsx('relative flex gap-3', dense ? 'pb-2.5' : 'pb-4', i === items.length - 1 && '!pb-0')}>
            <span className={clsx('relative z-[1] mt-1 w-3.5 h-3.5 rounded-full border-2 border-white flex items-center justify-center shrink-0',
              DOT[it.tone] || DOT.slate)}>
              {ItIcon && <ItIcon size={8} className="text-white" />}
            </span>
            <div className="min-w-0 flex-1 -mt-0.5">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[12.5px] text-slate-800">
                  {it.actor && <span className="font-semibold">{it.actor}</span>}{it.actor ? ' ' : ''}{it.action}
                </span>
                <time className="ml-auto text-[11px] text-slate-400 tabular-nums whitespace-nowrap" dateTime={it.at ? String(it.at) : undefined}>
                  {fmtDateTime(it.at)}
                </time>
              </div>
              {it.note && <p className="text-[12px] text-slate-500 mt-0.5">{it.note}</p>}
              {it.diff && it.diff.length > 0 && (
                <div className="mt-1.5 rounded-lg border border-slate-200 bg-slate-50/70 divide-y divide-slate-200">
                  {it.diff.filter(Boolean).map((d) => (
                    <div key={d.field} className="flex items-center gap-2 px-2.5 py-1.5 text-[11.5px]">
                      <MicroLabel className="w-28 shrink-0">{d.field}</MicroLabel>
                      <span className="text-slate-400 line-through truncate">{d.from == null || d.from === '' ? '—' : String(d.from)}</span>
                      <ArrowRight size={11} className="text-slate-300 shrink-0" />
                      <span className="text-slate-800 font-medium truncate">{d.to == null || d.to === '' ? '—' : String(d.to)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ─── Block reasons ─────────────────────────────────────────────────────────
// Spec 6.2: "The invoice shows every block reason at once so the user does not
// fix one problem and hit the next." So: no accordion, no first-reason-only —
// every reason is listed, each with its own suggested next step and action.
// Accepts plain strings too, so a legacy `on_hold_reason` still renders.

const SEVERITY = { block: 'rose', warn: 'amber', info: 'slate' };

export function BlockReasonList({ reasons = [], title, onRecheck, recheckLabel = 'Re-check now', emptyLabel, className }) {
  const list = (reasons || []).filter(Boolean).map((r, i) => (typeof r === 'string'
    ? { code: `reason-${i}`, label: r, severity: 'block' }
    : { code: r.code || r.label || `reason-${i}`, severity: 'block', ...r }));

  if (!list.length) {
    if (!emptyLabel) return null;
    return (
      <div className={clsx('flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-2.5', className)}>
        <Check size={14} className="text-emerald-600 shrink-0" />
        <p className="text-[12.5px] font-medium text-emerald-700">{emptyLabel}</p>
      </div>
    );
  }

  const worst = list.some((r) => r.severity === 'block') ? 'rose' : list.some((r) => r.severity === 'warn') ? 'amber' : 'slate';
  const n = list.length;
  return (
    <div className={clsx('rounded-xl border bg-white overflow-hidden', TONE_BORDER[worst], className)}>
      <div className={clsx('flex items-center gap-2 px-4 py-2.5 border-b', TONE_HEAD[worst])}>
        <AlertTriangle size={14} className={clsx('shrink-0', TONE_TEXT[worst])} />
        <p className={clsx('text-[12.5px] font-semibold', TONE_TEXT[worst])}>
          {title || `${n} ${n === 1 ? 'reason' : 'reasons'} this invoice cannot move forward`}
        </p>
        {onRecheck && (
          <button type="button" onClick={onRecheck} className="ml-auto btn-secondary btn-sm">
            <RefreshCw size={12} />{recheckLabel}
          </button>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {list.map((r, i) => (
          <li key={r.code} className="flex items-start gap-3 px-4 py-3">
            <span className={clsx('mt-0.5 w-5 h-5 rounded-md text-[10.5px] font-bold tabular-nums flex items-center justify-center shrink-0',
              TONE_SOFT[SEVERITY[r.severity] || 'rose'])}>{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-slate-800">{r.label}</p>
              {r.detail && <p className="text-[12px] text-slate-500 mt-0.5">{r.detail}</p>}
              {r.nextStep && (
                <p className="text-[12px] text-slate-600 mt-1 flex items-start gap-1.5">
                  <ArrowRight size={12} className="mt-0.5 shrink-0 text-slate-400" />
                  <span><span className="font-semibold text-slate-500">Next step</span> — {r.nextStep}</span>
                </p>
              )}
              {r.recheckAt && (
                <span className="badge badge-gray !text-[10.5px] mt-1.5">Will re-check on {fmtDate(r.recheckAt)}</span>
              )}
            </div>
            {r.action && (
              <button type="button" onClick={r.action.onClick} className="btn-secondary btn-sm shrink-0">{r.action.label}</button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Money stats ───────────────────────────────────────────────────────────
// The budget position an operator checks before releasing anything:
// Released · Committed · Available now · Next release (spec 4.3).

const STAT_SIZE = { sm: 'text-[14px]', md: 'text-[17px]', lg: 'text-[21px]' };

export function MoneyStat({ label, value, sub, tone = 'slate', size = 'md', align = 'left', currency = true, icon: Icon, className }) {
  const isNum = typeof value === 'number';
  return (
    <div className={clsx('min-w-0', align === 'right' && 'text-right', className)}>
      <MicroLabel className={clsx('flex items-center gap-1', align === 'right' && 'justify-end')}>
        {Icon && <Icon size={11} className="text-slate-300" />}{label}
      </MicroLabel>
      <div className={clsx('font-semibold tabular-nums leading-tight mt-1', STAT_SIZE[size] || STAT_SIZE.md,
        tone === 'slate' ? 'text-slate-900' : TONE_TEXT[tone])}>
        {isNum && currency ? money(value) : (value == null || value === '' ? '—' : value)}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

// Dividers only appear at the breakpoint where every stat is on one row —
// a hairline hanging off a wrapped column reads as a mistake.
const STAT_GRID = {
  1: { cols: 'grid-cols-1', div: '', pad: '' },
  2: { cols: 'grid-cols-2', div: 'divide-x divide-slate-100', pad: 'px-4 first:pl-0' },
  3: { cols: 'grid-cols-2 sm:grid-cols-3', div: 'sm:divide-x sm:divide-slate-100', pad: 'sm:px-4 sm:first:pl-0' },
  4: { cols: 'grid-cols-2 lg:grid-cols-4', div: 'lg:divide-x lg:divide-slate-100', pad: 'lg:px-4 lg:first:pl-0' },
  5: { cols: 'grid-cols-2 lg:grid-cols-5', div: 'lg:divide-x lg:divide-slate-100', pad: 'lg:px-4 lg:first:pl-0' },
};

export function StatRow({ cols = 4, divided = true, children, className }) {
  const g = STAT_GRID[cols] || STAT_GRID[4];
  const kids = React.Children.toArray(children);
  return (
    <div className={clsx('grid gap-x-4 gap-y-4', g.cols, divided && g.div, className)}>
      {kids.map((c, i) => <div key={c.key || i} className={clsx('min-w-0', divided && g.pad)}>{c}</div>)}
    </div>
  );
}

// ─── Form primitives ───────────────────────────────────────────────────────

// `children` may be a node or a render function receiving the generated id.
export function Field({ label, hint, error, required, children, htmlFor, action, className }) {
  const auto = useId();
  const id = htmlFor || auto;
  return (
    <div className={clsx('min-w-0', className)}>
      <div className="flex items-center gap-2 mb-1">
        <label htmlFor={id} className="text-[13px] font-medium text-slate-700">
          {label}{required && <span className="text-rose-500 ml-0.5" aria-hidden>*</span>}
        </label>
        {action && <span className="ml-auto">{action}</span>}
      </div>
      {typeof children === 'function' ? children(id) : children}
      {error ? <p className="text-[12px] text-rose-600 mt-1" role="alert">{error}</p>
        : hint ? <p className="text-[12px] leading-relaxed text-slate-500 mt-1">{hint}</p> : null}
    </div>
  );
}

const FORM_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-1 sm:grid-cols-2', 3: 'grid-cols-1 sm:grid-cols-3', 4: 'grid-cols-2 sm:grid-cols-4' };

export function FormRow({ cols = 2, children, className }) {
  return <div className={clsx('grid gap-x-4 gap-y-3.5', FORM_COLS[cols] || FORM_COLS[2], className)}>{children}</div>;
}

// Click-to-edit cell. Enter or blur commits, Escape reverts — correcting an
// extracted value should never cost a modal (spec 6.5).
export function InlineEdit({
  value, onSave, placeholder = '—', type = 'text', align = 'left', format, disabled,
  ariaLabel = 'Edit value', className, inputClassName,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const inputRef = useRef(null);
  useEffect(() => { if (!editing) setDraft(value == null ? '' : String(value)); }, [value, editing]);
  useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (onSave && next !== (value == null ? '' : String(value))) onSave(type === 'number' ? Number(next) : next);
  };

  if (editing) {
    return (
      <input
        ref={inputRef} type={type} value={draft} aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)} onBlur={commit}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); setDraft(value == null ? '' : String(value)); setEditing(false); }
        }}
        className={clsx('w-full h-7 px-1.5 text-[13px] bg-white border border-primary-400 rounded-md text-slate-900',
          'focus:outline-none focus:ring-2 focus:ring-primary-100', type === 'number' && 'tabular-nums text-right',
          ALIGN[align], inputClassName)}
      />
    );
  }
  const shown = format ? format(value) : (value == null || value === '' ? placeholder : String(value));
  return (
    <button
      type="button" disabled={disabled} aria-label={ariaLabel}
      onClick={(e) => { e.stopPropagation(); if (!disabled) setEditing(true); }}
      className={clsx('group inline-flex items-center gap-1.5 max-w-full h-7 px-1.5 -mx-1.5 rounded-md text-[13px] text-slate-800',
        'hover:bg-slate-100 disabled:hover:bg-transparent disabled:cursor-default transition-colors motion-reduce:transition-none',
        align === 'right' && 'flex-row-reverse', type === 'number' && 'tabular-nums', className)}>
      <span className="truncate">{shown}</span>
      {!disabled && <Pencil size={11} className="shrink-0 text-slate-300 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" />}
    </button>
  );
}
